import type { EmployeeDesignation, ExecutiveBriefingProfile, UserRole } from '../types';
import { hasEmployeeDesignation, hasOfficeOpsPrivileges, isCooDesignation } from './employeeDesignations';

export type PurchasingActorAccess = {
  authRole?: string;
  designations?: EmployeeDesignation[] | string[];
  briefingProfile?: ExecutiveBriefingProfile | null;
};

export function canAccessPurchasing(role: UserRole | string | undefined): boolean {
  const r = String(role || '').toLowerCase();
  return (
    r === 'employee'
    || r === 'admin'
    || r === 'staff'
    || r === 'employee_shop'
    || r === 'employee_office'
  );
}

/** Office staff process the company purchasing queue (not shop / field). */
export function isPurchasingOfficeProcessor(args: PurchasingActorAccess): boolean {
  return String(args.authRole || '').toLowerCase() === 'employee_office';
}

/** Admin, COO, CFO, EA, and Office Manager — skip DoA and see budget remaining. */
export function canOverridePurchaseApproval(args: PurchasingActorAccess): boolean {
  const role = String(args.authRole || '').toLowerCase();
  if (role === 'admin') return true;
  if (isCooDesignation(args)) return true;
  return (
    hasEmployeeDesignation(args.designations, 'cfo')
    || hasOfficeOpsPrivileges(args.designations)
  );
}

export function canViewAllPurchaseRequisitions(args: PurchasingActorAccess): boolean {
  return isPurchasingOfficeProcessor(args) || canOverridePurchaseApproval(args);
}

/** Mark purchased / receipts on any issued PO without skipping approval gates. */
export function canProcessCompanyPurchases(args: PurchasingActorAccess): boolean {
  return isPurchasingOfficeProcessor(args) || canOverridePurchaseApproval(args);
}

function isShopPurchasingRole(args: PurchasingActorAccess): boolean {
  return String(args.authRole || '').toLowerCase() === 'employee_shop';
}

function shopCanCompletePurchaseRow(
  args: PurchasingActorAccess,
  row: {
    requesterId?: string;
    approvalSteps?: Array<{ userId?: string }>;
    approvalStepUserIds?: string[];
  },
  actorId: string,
): boolean {
  if (!isShopPurchasingRole(args)) return false;
  if (row.requesterId === actorId) return true;
  return actorOnPurchaseApprovalTrail(row, actorId);
}

function actorOnPurchaseApprovalTrail(
  row: {
    approvalSteps?: Array<{ userId?: string }>;
    approvalStepUserIds?: string[];
  },
  actorId: string,
): boolean {
  if (Array.isArray(row.approvalStepUserIds) && row.approvalStepUserIds.includes(actorId)) {
    return true;
  }
  return (row.approvalSteps || []).some((step) => step.userId === actorId);
}

export function canEditPurchasingPolicy(args: PurchasingActorAccess): boolean {
  const role = String(args.authRole || '').toLowerCase();
  if (role === 'admin') return true;
  if (isCooDesignation(args)) return true;
  return hasEmployeeDesignation(args.designations, 'cfo');
}

export function canEditOrgChart(args: PurchasingActorAccess): boolean {
  const role = String(args.authRole || '').toLowerCase();
  if (role === 'admin') return true;
  if (isCooDesignation(args)) return true;
  return hasOfficeOpsPrivileges(args.designations);
}

export function canReviewPurchaseRequisition(
  args: PurchasingActorAccess,
  row: { requesterId?: string; currentApproverId?: string; approvalStepUserIds?: string[] },
  actorId: string,
): boolean {
  if (canOverridePurchaseApproval(args)) return true;
  if (row.currentApproverId === actorId) return true;
  return Array.isArray(row.approvalStepUserIds) && row.approvalStepUserIds.includes(actorId);
}

export function canUploadPurchaseReceipt(
  args: PurchasingActorAccess,
  row: {
    requesterId?: string;
    currentApproverId?: string;
    approvalStepUserIds?: string[];
    approvalSteps?: Array<{ userId?: string }>;
    status: string;
    purchaseVariance?: { reviewerId?: string };
  },
  actorId: string,
): boolean {
  if (row.status !== 'approved' && row.status !== 'purchased') return false;
  if (canProcessCompanyPurchases(args)) return true;
  if (shopCanCompletePurchaseRow(args, row, actorId)) return true;
  if (row.purchaseVariance?.reviewerId === actorId) return true;
  if (row.requesterId === actorId) return true;
  if (actorOnPurchaseApprovalTrail(row, actorId)) return true;
  return canReviewPurchaseRequisition(args, row, actorId);
}

/** Add/remove quote screenshots & docs on an open requisition (not cancelled). */
export function canManagePurchaseSupportingAttachments(
  args: PurchasingActorAccess,
  row: {
    requesterId?: string;
    currentApproverId?: string;
    approvalStepUserIds?: string[];
    status: string;
  },
  actorId: string,
): boolean {
  if (row.status === 'cancelled') return false;
  if (canProcessCompanyPurchases(args)) return true;
  return actorOwnsOrReviewsPurchase(args, row, actorId);
}

/** Delete a receipt the same people who could upload one. */
export function canRemovePurchaseReceipt(
  args: PurchasingActorAccess,
  row: {
    requesterId?: string;
    currentApproverId?: string;
    approvalStepUserIds?: string[];
    status: string;
    purchaseVariance?: { reviewerId?: string };
  },
  actorId: string,
): boolean {
  return canUploadPurchaseReceipt(args, row, actorId);
}

export function canReviseIssuedPurchaseAmount(
  args: PurchasingActorAccess,
  row: {
    requesterId?: string;
    currentApproverId?: string;
    approvalStepUserIds?: string[];
    status: string;
    hold?: unknown;
    purchaseVariance?: { reviewerId?: string };
  },
  actorId: string,
): boolean {
  if (row.status !== 'approved') return false;
  if (row.hold) return false;
  if (row.purchaseVariance) return false;
  return actorOwnsOrReviewsPurchase(args, row, actorId);
}

/** Edit amount / purchasedAmount on an already-purchased PO (note + audit required). */
export function canEditPurchasedPurchaseAmounts(
  args: PurchasingActorAccess,
  row: {
    requesterId?: string;
    currentApproverId?: string;
    approvalStepUserIds?: string[];
    status: string;
  },
  actorId: string,
): boolean {
  if (row.status !== 'purchased') return false;
  if (canProcessCompanyPurchases(args)) return true;
  return actorOwnsOrReviewsPurchase(args, row, actorId);
}

export function canMarkPurchasePurchased(
  args: PurchasingActorAccess,
  row: {
    requesterId?: string;
    currentApproverId?: string;
    approvalStepUserIds?: string[];
    approvalSteps?: Array<{ userId?: string }>;
    status: string;
    purchaseVariance?: { reviewerId?: string };
  },
  actorId: string,
): boolean {
  if (row.status !== 'approved') return false;
  if (canProcessCompanyPurchases(args)) return true;
  if (shopCanCompletePurchaseRow(args, row, actorId)) return true;
  if (row.purchaseVariance?.reviewerId === actorId) return true;
  if (row.requesterId === actorId) return true;
  if (actorOnPurchaseApprovalTrail(row, actorId)) return true;
  return canReviewPurchaseRequisition(args, row, actorId);
}

function actorOwnsOrReviewsPurchase(
  args: PurchasingActorAccess,
  row: { requesterId?: string; currentApproverId?: string; approvalStepUserIds?: string[] },
  actorId: string,
): boolean {
  if (row.requesterId === actorId) return true;
  return canReviewPurchaseRequisition(args, row, actorId);
}

export function canCancelPurchaseRequisition(
  args: PurchasingActorAccess,
  row: { requesterId?: string; currentApproverId?: string; approvalStepUserIds?: string[]; status: string },
  actorId: string,
): boolean {
  if (row.status === 'cancelled') return false;
  return actorOwnsOrReviewsPurchase(args, row, actorId);
}

export function canDeletePurchaseRequisition(
  args: PurchasingActorAccess,
  row: { requesterId?: string; currentApproverId?: string; approvalStepUserIds?: string[] },
  actorId: string,
): boolean {
  return actorOwnsOrReviewsPurchase(args, row, actorId);
}

export function canReassignPurchaseRequester(args: PurchasingActorAccess): boolean {
  return canViewAllPurchaseRequisitions(args);
}

/** Budget remaining cards are for executives (admin, CFO, COO, EA, Office Manager) during review. */
export function canViewPurchaseBudgets(args: PurchasingActorAccess): boolean {
  return canOverridePurchaseApproval(args);
}
